<template>
  <master-layout pageTitle="Dashboard">
    <div class="container">
      <div class="flex-between">
        <h1 class="vtext-hero-title"> Welcome back<span v-if="roleDetails?.name">, {{ (roleDetails.name.split(" "))[0] }} </span> 👋 </h1>
      </div>
      <div class="dashboard">
        <div v-if="socialPlatformConnected" class="vmb-20">
          <p class="vtext-card-title--light vpb-10">Connected Channel(s)</p>
          <div v-if="whatsappConnected" class="sales-channel-container flexContainer">
            <div class="flex-start">
              <div class="image image-with-badge">
                <img src="@/resources/images/whatsapp_icon.svg" alt="whatsapp"/>
                <VerificationBadge
                  class="channel-verification-badge"
                  :verified="isChannelVerified('whatsapp')"
                  :error-message="verificationErrorFor('whatsapp')"
                />
              </div>
              <div v-if="whatsAppSocialData" class="sales-channel-text cursorPointer" @click="handleChannelClick('/sales-channel/whatsapp')">
                <div class="name">WhatsApp</div>
                <div class="flex-start paddingTop flex-wrap">
                  <p class="primary-title vtext-primary"> {{ whatsAppSocialData.name}} </p>
                  <p class="linkTitle">({{ whatsAppSocialData.country_code }} {{ whatsAppSocialData.mobile_number }})</p>
                </div>
              </div>
            </div>
            <VBadge connotation="success" text="Connected" appearance="subtle" size="expanded"></VBadge>
          </div>
          <div v-if="googlercsConnected" class="sales-channel-container flexContainer">
            <div class="flex-start">
              <div class="image image-with-badge">
                <img src="@/resources/images/Google_RCS_icon.svg" alt="googlercs_logo" width="40"/>
                <VerificationBadge
                  class="channel-verification-badge"
                  :verified="isChannelVerified('googlercs')"
                  :error-message="verificationErrorFor('googlercs')"
                />
              </div>
              <div class="sales-channel-text cursorPointer" @click="handleChannelClick('/sales-channel/google-rcs')">
                <div class="name">Google RCS</div>
                <p class="primary-title paddingTop flex-wrap">{{ googlercsSocialData.number }}</p>
              </div>
            </div>
            <VBadge connotation="success" text="Connected" appearance="subtle" size="expanded"></VBadge>
          </div>
          <div v-if="smsConnected" class="sales-channel-container flexContainer">
            <div class="flex-start">
              <div class="image image-with-badge">
                <img src="@/resources/images/sms_logo.svg" alt="smsLogo"/>
                <VerificationBadge
                  class="channel-verification-badge"
                  :verified="isChannelVerified('sms')"
                  :error-message="verificationErrorFor('sms')"
                />
              </div>
              <div class="sales-channel-text cursorPointer" @click="handleChannelClick('/sales-channel/sms')">
                <div class="name">SMS</div>
                <div class="flex-start paddingTop flex-wrap">
                  <p class="primary-title">{{ smsSocialData.account_name || smsSocialData.appname }}</p>
                </div>
              </div>
            </div>
            <VBadge connotation="success" appearance="subtle" text="Connected" size="expanded"></VBadge>
          </div>
          <div v-if="emailConnected" class="sales-channel-container flexContainer">
            <div class="flex-start">
              <div class="image image-with-badge">
                <img src="@/resources/images/email_icon.svg" alt="email_logo"/>
                <VerificationBadge
                  class="channel-verification-badge"
                  :verified="isChannelVerified('email')"
                  :error-message="verificationErrorFor('email')"
                />
              </div>
              <div class="sales-channel-text cursorPointer" @click="handleChannelClick('/sales-channel/email')">
                <div class="name">Email</div>
                <div class="flex-start paddingTop flex-wrap">
                  <p class="primary-title">{{ emailSocialData?.appname || emailSocialData?.account_name || emailSocialData?.from_email || 'Email' }}</p>
                </div>
              </div>
            </div>
            <VBadge connotation="success" appearance="subtle" text="Connected" size="expanded"></VBadge>
          </div>
        </div>
        <p class="vtext-card-title--light vpb-10">Help center</p>
        <div class="flex-between">
          <div class="help-container flex100P flex-between">
            <div class="flex-start gap20P flex200P">
              <div>
                <img src="@/resources/images/Help.svg" class="help-img" />
              </div>
              <div>
                <p class="help-title">Find answers quickly</p>
                <p class="vtext-subtitle">Visit our Knowledge Base to find guides and troubleshooting tips.</p>
              </div>
            </div>
            <VButton class="vvd-btn-secondary" connotation="cta" appearance="outlined" label="Visit Knowledge Base" icon="arrow-right-line" @click="knowledgeBaseRedirect"></VButton>
          </div>
          <div class="help-container flex100P flex-between">
            <div class="flex-start gap20P flex200P">
              <div>
                <img src="@/resources/images/submit_ticket.svg" class="help-img" />
              </div>
              <div>
                <p class="help-title">Need more help?</p>
                <p class="vtext-subtitle"> Submit a support ticket or reach us at <a href="mailto:support@api.vonage.com">support@api.vonage.com</a> for assistance. Please include your Account ID <b>"{{ user?.referallshopName }}"</b> in your email or ticket.</p>
              </div>
            </div>
            <VButton class="vvd-btn-secondary" connotation="cta" appearance="outlined" label="Submit ticket" icon="arrow-right-line" @click="raiseTicket"></VButton>
          </div>
        </div>
      </div>
    </div>
  </master-layout>
</template>

<script>
import '/src/resources/css/dashboard.css';

import apiInterceptor from '@/shared/apiInterceptor';
import { queryClient } from '@/shared/queryClient';
import { environment } from '/src/shared/env';
import { useUIStore } from '@/stores/ui';
import { useAuthStore } from '@/stores/auth';
import { useSocialChannels } from '@/composables/useSocialChannels';
import { useConnectionVerification } from '@/composables/useConnectionVerification';
import { useRoleDetails } from '@/composables/useRoleDetails';
import VerificationBadge from '@/components/VerificationBadge.vue';

export default {
  components: {
    VerificationBadge
  },
  setup() {
    const { whatsappConnected, whatsAppSocialData, googlercsConnected, googlercsSocialData, smsConnected, smsSocialData, emailConnected, emailSocialData, socialPlatformConnected } = useSocialChannels();
    const { roleDetails } = useRoleDetails();
    // Verify ALL channels for the dashboard (no channel filter).
    const { isChannelVerified, verificationErrorFor } = useConnectionVerification();
    return { whatsappConnected, whatsAppSocialData, googlercsConnected, googlercsSocialData, smsConnected, smsSocialData, emailConnected, emailSocialData, socialPlatformConnected, roleDetails, isChannelVerified, verificationErrorFor };
  },
  async mounted(){
    try {
      useUIStore().updateLoading({ isLoading: true, message: 'Loading dashboard...' });
      //save timezone in add-user API - start
      if(this.isAdmin && (useAuthStore().cleanUserData?.timezone == null || useAuthStore().cleanUserData?.timezone == 'None' || useAuthStore().cleanUserData?.timezone == '')){
        let reqBody = {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        }
        await apiInterceptor.patch( environment("VUE_APP_API_URL") + "settings/v1/account/", reqBody );
        if (useAuthStore().cleanUserData) {
          useAuthStore().cleanUserData.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        }
        await queryClient.invalidateQueries({ queryKey: ['account-settings'] });
        await queryClient.invalidateQueries({ queryKey: ['role-details'] });
        await useAuthStore().checkToken();
      }
      //save timezone in add-user API - end
    } catch (error) {
      useUIStore().updateToast({
        open: true,
        message: error.message || "Something went wrong",
        type: "danger"
      });
    } finally {
      useUIStore().updateLoading({ isLoading: false });
    }
  },
  computed: {
    isAdmin() { return useAuthStore().isAdmin; },
    currency() {
      return useAuthStore().cleanUserData?.currency;
    },
    user() {
      return useAuthStore().cleanUserData;
    }
  },
  methods: {
    environment,
    handleChannelClick(route) {
      // Check if screen width is less than 992px (tablet breakpoint)
      if (window.innerWidth < 992) {
        // Disable click on mobile
        return;
      }
      // Allow navigation on tablet and above
      this.$router.push(route);
    },
    raiseTicket(){
      const ticketUrl = 'https://api.support.vonage.com/hc/en-us/requests/new?ticket_form_id=4426111571220';
      window.open(ticketUrl, '_blank', 'noopener,noreferrer');
    },
    knowledgeBaseRedirect() {
      window.open('https://api.support.vonage.com/hc/en-us/categories/20878939317788-Conversational-Connect-Jumper-ai', '_blank', 'noopener,noreferrer');
    }
  }
};
</script>

<style scoped>
.container {
  background: var(--background-primary);
  margin: 20px;
}

.dashboard {
  margin-top: 40px;
}

.primary-title {
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: var(--purple);
  margin: 0;
}

.image {
  width: 40px;
}

.image-with-badge {
  position: relative;
  display: inline-block;
  line-height: 0;
}

.channel-verification-badge {
  position: absolute;
  top: -6px;
  right: -6px;
}

.sales-channel-text {
  margin-left: 15px;
}

.sales-channel-text .name {
  font-style: normal;
  font-weight: 700;
  font-size: 18px;
  line-height: 21px;
  color: var(--black);
}

.sales-channel-container {
  background: var(--white);
  border-radius: 5px;
  padding: 20px;
  margin-bottom: 10px;
}

.help-container {
  background: var(--white);
  border-radius: 5px;
  padding: 20px;
  min-height: 130px;
}

.help-img {
  width: 150px;
  height: auto;
}

.help-container .help-title {
  font-weight: 700;
  font-size: 18px;
  line-height: 28px;
  color: var(--black);
  margin: 0;
}

.paddingTop {
  padding-top: 5px;
}

.linkTitle {
  color: var(--purple);
  font-size: 14px;
  font-weight: 400;
  margin: 0;
}

.flexContainer {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

a {
  text-decoration: none;
  color: var(--purple);
}

.flex-start {
  align-items: center;
}

b {
  color: var(--black);
}

.flex200P {
  flex: 2;
}

@media only screen and (max-width: 991px) {
  .sales-channel-text {
    pointer-events: none;
    cursor: default !important;
  }
}

@media only screen and (max-width: 900px) {
  .flex-between {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
